import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { FileText, Video as VideoIcon, Presentation, Lock, X, Shield, Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";
import PdfJsViewer from "@/components/PdfJsViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  pdf: { icon: FileText, label: "PDF", tint: "bg-[#DC2626]" },
  pptx: { icon: Presentation, label: "PowerPoint", tint: "bg-[#EA580C]" },
  video: { icon: VideoIcon, label: "Video", tint: "bg-[#0B3D91]" },
};

export default function Materials() {
  const { token, user } = useAuth();
  const { t } = useLang();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/materials`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMaterials(r.data))
      .catch((e) => { console.error("Materials load failed:", e); toast.error("Errore caricamento materiali"); })
      .finally(() => setLoading(false));
  }, [token]);

  // Anti-copy shortcuts + right-click block while viewing
  useEffect(() => {
    if (!viewing) return;
    const blockKeys = (e) => {
      const k = e.key.toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      if (mod && ["s", "p", "u", "c"].includes(k)) { e.preventDefault(); toast.info("Scaricamento non consentito"); }
      if (k === "printscreen") { e.preventDefault(); toast.info("Screenshot scoraggiato"); }
    };
    const blockCtx = (e) => e.preventDefault();
    window.addEventListener("keydown", blockKeys);
    window.addEventListener("contextmenu", blockCtx);
    return () => { window.removeEventListener("keydown", blockKeys); window.removeEventListener("contextmenu", blockCtx); };
  }, [viewing]);

  if (!token) {
    return (
      <div className="page-bg min-h-[60vh] flex items-center justify-center px-4">
        <div className="card-elevated p-8 max-w-md text-center fade-up">
          <Lock size={40} className="mx-auto text-[#F97316]" />
          <h1 className="mt-3 font-display font-extrabold text-2xl text-[#0B3D91]">Materiali riservati</h1>
          <p className="mt-2 text-sm text-[#5A6A94]">Accedi al tuo account per rivedere le lezioni caricate dall'associazione.</p>
          <Link to="/login" className="btn-primary mt-5 inline-flex"><LogIn size={16} /> Accedi</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-bg py-12 min-h-[80vh]">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-6 fade-up">
          <div className="w-11 h-11 rounded-xl brand-blue-bg text-white flex items-center justify-center"><Shield size={22} /></div>
          <div>
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0B3D91]" data-testid="materials-title">Materiali delle lezioni</h1>
            <p className="text-sm text-[#5A6A94]">Rivedi PDF, slide e video registrati dall'associazione. <strong>Scaricamento e condivisione non consentiti.</strong></p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#5A6A94]"><Loader2 className="animate-spin inline" size={24} /></div>
        ) : materials.length === 0 ? (
          <div className="card-elevated p-10 text-center fade-up" data-testid="no-materials">
            <p className="text-[#5A6A94]">Nessun materiale ancora caricato dall'admin.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {materials.map((m) => {
              const meta = KIND_META[m.kind] || KIND_META.pdf;
              return (
                <button key={m.id} onClick={() => setViewing(m)} className="card-elevated p-5 text-left hover:shadow-lg transition-all fade-up" data-testid={`material-card-${m.id}`}>
                  <div className={`w-11 h-11 rounded-xl ${meta.tint} text-white flex items-center justify-center mb-3`}><meta.icon size={22} /></div>
                  <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">{meta.label}{m.level ? ` · ${m.level}` : ""}</div>
                  <h3 className="mt-1 font-display font-bold text-lg text-[#0B3D91] line-clamp-2">{m.title}</h3>
                  {m.description && <p className="mt-1 text-xs text-[#5A6A94] line-clamp-2">{m.description}</p>}
                  <p className="mt-3 text-xs text-[#8494B8]">{new Date(m.created_at).toLocaleDateString("it-IT")} · {(m.size / (1024 * 1024)).toFixed(1)} MB</p>
                </button>
              );
            })}
          </div>
        )}

        {viewing && <MaterialViewer material={viewing} user={user} token={token} onClose={() => setViewing(null)} />}
      </div>
    </div>
  );
}

function MaterialViewer({ material, user, token, onClose }) {
  const src = `${API}/materials/${material.id}/view?_t=${token}`; // token in query for iframe/video src
  const wm = user?.email || "SS Europa by Amei";
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col select-none" onContextMenu={(e) => e.preventDefault()} data-testid="material-viewer">
      <div className="flex items-center justify-between px-5 py-3 bg-black/60 text-white">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">{material.kind.toUpperCase()}</div>
          <div className="font-semibold">{material.title}</div>
        </div>
        <div className="text-xs opacity-70 hidden sm:block">🔒 Solo visualizzazione — scaricamento e screenshot scoraggiati</div>
        <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="viewer-close-btn">
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 relative overflow-hidden">
        {/* Watermark overlay */}
        <div className="absolute inset-0 pointer-events-none z-10 flex flex-wrap items-center justify-center opacity-20" data-testid="viewer-watermark">
          {Array.from({ length: 20 }).map((_, i) => (
            <span key={`wm-${i}`} className="text-white text-xs px-8 py-16 whitespace-nowrap rotate-[-30deg]">{wm} · SS Europa</span>
          ))}
        </div>

        {material.kind === "pdf" && (
          <PdfJsViewer
            src={`${API}/materials/${material.id}/view`}
            token={token}
            title={material.title}
          />
        )}
        {material.kind === "video" && (
          <VideoPlayer src={src} material={material} />
        )}
        {material.kind === "pptx" && (
          <div className="w-full h-full flex items-center justify-center text-center text-white px-6">
            <div className="max-w-lg">
              <Presentation size={60} className="mx-auto text-[#F97316] mb-4" />
              <h3 className="font-display font-bold text-2xl mb-2">Anteprima PowerPoint non disponibile</h3>
              <p className="opacity-80 text-sm">Il file PowerPoint richiede software esterno. Chiedi all'admin di caricarlo anche come PDF per rivederlo qui.</p>
              <p className="mt-4 text-xs opacity-60">File: {material.filename} · {(material.size / (1024 * 1024)).toFixed(1)} MB</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function VideoPlayer({ src, material }) {
  const [failed, setFailed] = useState(false);
  const [key, setKey] = useState(0);
  if (failed) {
    const ext = (material.filename || "").split(".").pop().toLowerCase();
    const hint = ["mkv", "avi"].includes(ext)
      ? "Questo formato non è supportato da tutti i browser (soprattutto iPhone). Chiedi all'admin di caricarlo in MP4."
      : "Controlla la connessione e riprova. Se il problema continua, avvisa l'admin.";
    return (
      <div className="w-full h-full flex items-center justify-center text-center text-white px-6" data-testid="video-error">
        <div className="max-w-md">
          <VideoIcon size={48} className="mx-auto text-[#F97316] mb-3" />
          <h3 className="font-display font-bold text-xl mb-2">Il video non si riesce a riprodurre</h3>
          <p className="opacity-80 text-sm">{hint}</p>
          <button onClick={() => { setFailed(false); setKey((k) => k + 1); }} className="mt-4 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 text-sm" data-testid="video-retry-btn">Riprova</button>
        </div>
      </div>
    );
  }
  return (
    <video
      key={key}
      src={src}
      controls
      playsInline
      preload="metadata"
      controlsList="nodownload noremoteplayback"
      disablePictureInPicture
      className="w-full h-full object-contain bg-black"
      data-testid="video-viewer"
      onError={() => setFailed(true)}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}
